import React from 'react'
import ReactDOM from 'react-dom/client'
import { Auth0Provider } from '@auth0/auth0-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { initializeTheme } from './stores/themeStore'
import { setTokenGetter } from './lib/api'
import './index.css'

// Initialize theme
initializeTheme()

// ── TanStack Query client ─────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// ── TanStack Router ───────────────────────────────────────────────────────────
const router = createRouter({
  routeTree,
  context: { queryClient },
})

// Type-safe router context
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// ── Auth0 config ──────────────────────────────────────────────────────────────
const auth0Domain = import.meta.env['VITE_AUTH0_DOMAIN'] as string
const auth0ClientId = import.meta.env['VITE_AUTH0_CLIENT_ID'] as string
const auth0Audience = import.meta.env['VITE_AUTH0_AUDIENCE'] as string

const hasAuth0Config = Boolean(auth0Domain && auth0ClientId && auth0Audience)

// ── Root render ───────────────────────────────────────────────────────────────
const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element not found')

const root = ReactDOM.createRoot(rootElement)

if (hasAuth0Config) {
  // Production with Auth0
  root.render(
    <React.StrictMode>
      <Auth0Provider
        domain={auth0Domain}
        clientId={auth0ClientId}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: auth0Audience,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </Auth0Provider>
    </React.StrictMode>,
  )
} else {
  // Auth0 not configured — show a clear message in development so local
  // testing uses the real Auth0 flow. This prevents the app from silently
  // using a fake "quick-login" token which the backend rejects.
  root.render(
    <React.StrictMode>
      <div style={{padding:40,fontFamily:'sans-serif'}}>
        <h2>Auth0 not configured</h2>
        <p>
          To use the application locally, set the Auth0 environment variables
          in your <strong>.env</strong> (VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID,
          VITE_AUTH0_AUDIENCE) and restart the frontend so the real Auth0
          login flow is used.
        </p>
      </div>
    </React.StrictMode>,
  )
}
